import React from "react";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";

import getChampionCenteredImage from "@/game-tft/get-champion-centered-image.mjs";
import StaticTFT from "@/game-tft/static.mjs";
import TypeIcon from "@/game-tft/TypeIcon.jsx";
import {
  Cost,
  ToolTipBox,
  TooltipContainer,
  TooltipStatsContainer,
} from "@/game-tft/UnitToolTip.style.jsx";
import { useLocalizedChampions } from "@/game-tft/use-champions.mjs";
import { translateTraits } from "@/game-tft/use-traits.mjs";
import { getColorWithRange } from "@/game-tft/util-get-winrate-color.mjs";
import HextechMatchGoldIcon from "@/inline-assets/hextech-match-gold.svg";
import { WinRate } from "@/shared/InfiniteTable.style.jsx";
import { getLocale } from "@/util/i18n-helper.mjs";
import { useLazyTooltip } from "@/util/tooltip.mjs";

const UnitToolTip = ({
  children,
  champInfo = {},
  skinSetting = false,
  set,
  showTooltip = true,
  style,
  className = "",
  champName = "",
  stats,
}) => {
  const { t } = useTranslation();
  const localizedChampions = useLocalizedChampions(set);
  const locale = getLocale();
  const traitClass = (champInfo?.class || champInfo[set]?.class) ?? [];
  const traitOrigin = (champInfo?.origin || champInfo[set]?.origin) ?? [];
  const traits = traitClass.concat(traitOrigin).map((trait) => {
    const traitKey = StaticTFT.getTraitKey(trait, set);
    return {
      trait: traitKey,
      traitTranslated: translateTraits(t, traitKey, set),
    };
  });

  const tooltipRef = useLazyTooltip(() => {
    const map = {
      champKey: champInfo.apiKey,
      champName: champName
        ? champName
        : (localizedChampions?.[champInfo.apiKey]?.name ?? champInfo.name),
      cost: (champInfo?.cost || champInfo[set]?.cost) ?? 0,
      imgUrl: getChampionCenteredImage(
        champInfo.apiKey,
        skinSetting && set ? set : undefined,
      ),
      traitsRendered: "",
    };
    const tooltipRendered = ReactDOMServer.renderToStaticMarkup(
      <TooltipContainer className="tooltip-plain">
        <div className="unit-tooltip">
          <div className="main-area">
            <div className="imgOverlay" />
            <img src={map.imgUrl} alt={map.champKey} />
            <div className="unit-traits">
              {traits?.length
                ? traits.map(({ trait, traitTranslated }, idx) => (
                    <div className="unit-trait" key={idx}>
                      <TypeIcon name={trait} />
                      <span>{traitTranslated}</span>
                    </div>
                  ))
                : ""}
            </div>
          </div>
          <div className="hover-bottom">
            <div className="unit-name">
              <span>{map.champName}</span>
            </div>
            <Cost>
              <HextechMatchGoldIcon />
              {map.cost}
            </Cost>
          </div>
          <TooltipStats
            avgPlace={stats?.avgPlace}
            pickRate={stats?.pickRate}
            avgPlaceImprovement={stats?.avgPlaceImprovement}
            avgPlaceWith={stats?.avgPlaceWith}
            avgPlaceWithout={stats?.avgPlaceWithout}
            locale={locale}
            t={t}
          />
        </div>
      </TooltipContainer>,
    );
    return tooltipRendered;
  });
  return showTooltip && champInfo?.apiKey ? (
    <ToolTipBox
      ref={tooltipRef}
      data-place="bottom"
      className={className}
      style={style}
    >
      {children}
    </ToolTipBox>
  ) : (
    children
  );
};

export const TooltipStats = ({
  pickRate,
  avgPlace,
  avgPlaceImprovement,
  avgPlaceWith,
  avgPlaceWithout,
  locale,
  t,
}) => {
  if (
    !pickRate &&
    !avgPlace &&
    !avgPlaceImprovement &&
    !avgPlaceWith &&
    !avgPlaceWithout
  )
    return "";

  const pickRateDisplay = pickRate
    ? t("lol:percent", "{{number}}%", {
        number: (pickRate * 100).toLocaleString(locale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      })
    : undefined;

  const avgPlaceDisplay =
    avgPlace?.toLocaleString(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) ?? undefined;

  const avgPlaceImprovementDisplay =
    avgPlaceImprovement?.toLocaleString(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) ?? undefined;

  const avgPlaceWithDisplay =
    avgPlaceWith?.toLocaleString(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) ?? undefined;

  const avgPlaceWithoutDisplay =
    avgPlaceWithout?.toLocaleString(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }) ?? undefined;

  return (
    <TooltipStatsContainer>
      <hr />
      <div className="statsSection">
        {pickRateDisplay ? (
          <div className="statsRow">
            <div className="type-caption shade1">
              {t("common:pickRate", "Pick Rate")}
            </div>
            <div className="type-caption--bold shade1">
              <WinRate
                color={getColorWithRange({
                  value: pickRate,
                  low: 0,
                  high: 1,
                  positiveOnly: true,
                })}
              >
                {pickRateDisplay}
              </WinRate>
            </div>
          </div>
        ) : (
          ""
        )}
        {avgPlaceDisplay ? (
          <div className="statsRow">
            <div className="type-caption shade1">
              {t("common:avgPlace", "Avg. Place")}
            </div>
            <div className="type-caption--bold shade1">
              <WinRate
                color={getColorWithRange({
                  value: avgPlace,
                  low: 0,
                  high: 8,
                  lowerBetter: true,
                  positiveOnly: true,
                })}
              >
                {avgPlaceDisplay}
              </WinRate>
            </div>
          </div>
        ) : (
          ""
        )}
        {avgPlaceImprovementDisplay ? (
          <div className="statsRow">
            <div className="type-caption shade1">
              {t("tft:avgImprovement", "Avg Improvement")}
            </div>
            <div className="type-caption--bold shade1">
              <WinRate
                color={getColorWithRange({
                  value: avgPlaceImprovement,
                  low: 0,
                  high: 4,
                  positiveOnly: true,
                })}
              >
                {avgPlaceImprovementDisplay}
              </WinRate>
            </div>
          </div>
        ) : (
          ""
        )}
        {avgPlaceWithDisplay ? (
          <div className="statsRow">
            <div className="type-caption shade1">
              {t("tft:avgPlaceWith", "Avg Place - With")}
            </div>
            <div className="type-caption--bold shade1">
              <WinRate
                color={getColorWithRange({
                  value: avgPlaceWith,
                  low: 0,
                  high: 8,
                  lowerBetter: true,
                  positiveOnly: true,
                })}
              >
                {avgPlaceWithDisplay}
              </WinRate>
            </div>
          </div>
        ) : (
          ""
        )}
        {avgPlaceWithoutDisplay ? (
          <div className="statsRow">
            <div className="type-caption shade1">
              {t("tft:avgPlaceWithout", "Avg Place - Without")}
            </div>
            <div className="type-caption--bold shade1">
              <WinRate
                color={getColorWithRange({
                  value: avgPlaceWithout,
                  low: 0,
                  high: 8,
                  lowerBetter: true,
                  positiveOnly: true,
                })}
              >
                {avgPlaceWithoutDisplay}
              </WinRate>
            </div>
          </div>
        ) : (
          ""
        )}
      </div>
    </TooltipStatsContainer>
  );
};

export default UnitToolTip;
